import { useState } from 'react';
import { Button } from '@components/Button';
import { IconClose } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Input } from '@components/Input';
import { PlatformIcon } from '@components/PlatformIcon';
import styles from './AddListModal.module.scss';

export interface AddListModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Close handler */
  onClose: () => void;

  /**
   * Called when user clicks Save with a non-empty Apollo List ID.
   * Should reject to surface an inline error (e.g. duplicate list).
   */
  onSave: (apolloListId: string) => Promise<void> | void;
}

// Apollo list IDs are 24-char lowercase hex. Enforced strictly: input is
// filtered to allowed characters + max length, and Add List stays disabled
// until the full 24-char ID is present.
const APOLLO_ID_REGEX = /^[a-f0-9]{24}$/;
const APOLLO_ID_ALLOWED_CHAR = /[a-f0-9]/g;
const APOLLO_ID_MAX_LEN = 24;

const AddListForm = ({ onClose, onSave }: Pick<AddListModalProps, 'onClose' | 'onSave'>) => {
  const [listId, setListId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (next: string) => {
    // Force lowercase, strip disallowed chars, cap at 24. This makes it
    // impossible to type an out-of-format value in the first place.
    const cleaned = (next.toLowerCase().match(APOLLO_ID_ALLOWED_CHAR) ?? [])
      .join('')
      .slice(0, APOLLO_ID_MAX_LEN);
    setListId(cleaned);
    if (error) setError(null);
  };

  const isValid = APOLLO_ID_REGEX.test(listId);

  const handleSave = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(listId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add list.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSubmitting) onClose();
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !isSubmitting) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && isValid && !isSubmitting) handleSave();
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-list-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          handleKeyDown(e);
        }}
      >
        <div className={styles.header}>
          <h2 id="add-list-title" className={styles.title}>
            Add Apollo List
          </h2>
          <IconButton
            size="small"
            aria-label="Close modal"
            onClick={onClose}
            className={styles.closeButton}
            disabled={isSubmitting}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        <div className={styles.content}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Apollo List ID</span>
            <Input
              value={listId}
              onChange={handleChange}
              placeholder="e.g. 6a6741511eca43001cc0f0a8"
              clearable
              leadingIcon={<PlatformIcon platform="apollo" />}
              className={styles.input}
              disabled={isSubmitting}
            />
            <p className={styles.helper}>
              24 lowercase hex characters ({listId.length}/{APOLLO_ID_MAX_LEN}).
            </p>
          </div>
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add List'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AddListModal = ({ isOpen, onClose, onSave }: AddListModalProps) => {
  if (!isOpen) return null;
  return <AddListForm onClose={onClose} onSave={onSave} />;
};
